import { Component, Inject, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { WINDOW_INJECTION_TOKEN } from 'src/app/injection-tokens';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { Dashboard, DashboardResponsiveApiModelSparse, DashboardsResponsiveExistingApiModel } from 'src/shared/models/dashboard.model';
import { DashboardService } from '../../dashboard.service';
import { DashboardProjectionSettingsModel } from './dashboard-projection-content.models';
import { DashboardProjectionService } from './dashboard-projection.service';

@Component({
    selector: 'cv-dashboard-projection-content',
    templateUrl: './dashboard-projection-content.component.html',
    styleUrls: ['./dashboard-projection-content.component.scss']
})
export class DashboardProjectionContentComponent implements OnInit {

    /** DOM reference to the content of the modal */
    @ViewChild('projectionSettingsModal') modalContentRef: TemplateRef<any>;

    /** Flag for if the page is loading */
    public isLoading = true;
    /** Flag for if the settings are saving */
    public isSaving = false;
    /** Flag to prevent the dashboard from loading before the projection starts */
    public isSettingsOpen = false;
    /** Boolean flag for if the projection is currently auto-swiping */
    public isPlaying = false;
    /** Holds the properties for all of the settings (minus the filteredDashboardsOnModal which is cached here instead) */
    public settings: DashboardProjectionSettingsModel;
    /** The search term for the modal (to filter dashboards that can be selected) */
    public searchTerm = "";
    /** List of dashboards to show that are available to select */
    public filteredDashboardsOnModal: DashboardResponsiveApiModelSparse[] = [];
    /** Default seconds for the auto swipe interval */
    public defaultSwipeSeconds = "30";
    /** Default minutes for the auto swipe interval */
    public defaultSwipeMinutes = "00";
    public hasInitialized = false;

    public dashboardSelected: boolean = false;
    public noData: boolean = false;
    public noAccess: boolean = false;

    /**tab controls */
    public favoriteOpen = false;
    public dashboardOpen = false;
    public placeholder: string = '';
    public dashboardTime: string = '';

    public autoSwipeLabel1: string = '';
    public autoSwipeLabel2: string = '';

    /** States for validation errors as well as the messages to show when they are visible */
    public validationErrors = {
        requiredDashboard: {
            showing: false,
            message: '',
        },
        swipeTime: {
            showing: false,
            message: '',
        }
    };

    /** Private interval timer to show the current time on the page */
    private dashboardClockTimer: number = null;
    /** Private interval timer to keep a reference to be able to use clearInterval */
    private intervalTimer: number = null;
    /** Set to the date value the interval last started */
    private lastIntervalStart: number = null;
    /** Used to track the time since the last interval when pausing the autoswipe */
    private secondsElapsed: number = 0;
    /** The index of the current dashboard. When this increments, the projection moves to the next dashboard */
    private index = 0;
    /** API Reference to the modal */
    private modalRef: NgbModalRef;

    private minimumSwipeDelaySeconds = 20;

    private subscriptions: Subscription[] = [];

    /** Options used for ngbModal */
    private readonly ngbModalOptions: NgbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        size: 'xl',
        modalDialogClass: 'modal-dialog-centered cx-one-ui',
        windowClass: 'cx-one-ui'
    };

    constructor(
        @Inject(WINDOW_INJECTION_TOKEN) private $window: Window,
        private dashboardService: DashboardService,
        private ngbModal: NgbModal,
        private projectionService: DashboardProjectionService,
        private cvLocalizationService: CVLocalizationService,
        private cvConfigurationService: CvConfigurationService,
        private timezoneService: TimezoneService,
    ) {
        this.cvConfigurationService.isCxOne = true;
        this.dashboardService.isDashboardProjection = true;
        this.subscriptions.push(this.dashboardService.dashboardLoadFailed.subscribe(failedKey => {
            const selectedDashIndex = this.settings.selectedDashboardObjects.findIndex(dash => dash.DashboardKey === failedKey);
            this.settings.selectedDashboards.splice(selectedDashIndex, 1);
            this.index = this.index % this.settings.selectedDashboards.length || 0;
            const loadedDashIndex = this.settings.loadedDashboardObjects.findIndex(dash => dash.DashboardKey === failedKey);
            this.settings.loadedDashboards.splice(loadedDashIndex, 1);
            this.activateDashboard();
        }));

        this.openFavorites();
    }

    /** Wrapper to get the current dashboard (from selectedDashboards[index]) */
    public get dashboard(): DashboardResponsiveApiModelSparse {
        if (this.isLoading || this.settings?.selectedDashboards == null || this.settings?.selectedDashboards.length === 0 || 
            this.settings.selectedDashboardObjects == null || this.settings.selectedDashboardObjects.length === 0)
            return null;
        return this.settings.selectedDashboardObjects[this.index] || null;
    }

    /** Wrapper to get the current dashboard name (from selectedDashboards[index]) */
    public get dashboardName() {
        if (this.isLoading || this.isSettingsOpen || this.settings?.selectedDashboards == null || this.settings?.selectedDashboards?.length === 0
            || this.settings?.selectedDashboardObjects == null || this.settings?.selectedDashboardObjects?.length === 0)
            return "";
        return this.settings.selectedDashboardObjects[this.index]?.DashboardName || "";
    }

    /** Getter to localize the header that says 'Selected Dashboards (X)' */
    public get selectedDashboardsAndCountHeader(): string {
        const defaultString = 'Selected Dashboards ({0})';
        const text = this.isLoading ? defaultString :
            this.cvLocalizationService.getCachedText("Dashboard_Projection", "Selected_Dashboards_Count", defaultString);
        return text.replace("{0}", this.settings.temp.selectedDashboards.length.toString());
    }

    /** Getter to find the hour offset of the user's selected timezone */
    public get timezoneOffset(): number {
        return this.timezoneService.getTimezoneOffset(this.timezoneService.getUserTimezoneInfo()?.selectedTimeZone) / 60; 
    }

    /** Gets the aria-label for removing a dashboard from the selected list. */
    public getDashboardRemoveAriaLabel(dashboardName: string): string {
        const defaultString = '{0}. Remove.';
        const text = this.isLoading ? defaultString :
            this.cvLocalizationService.getCachedText("Dashboard_Projection", "Aria_Dashboard_Remove", defaultString);
        return text.replace("{0}", dashboardName);
    }

    /** Activate the current dashboard. This function passes info from the dashboard set in the 'dashboard' property to the dashboard service */
    async activateDashboard() {
        if (this.settings.selectedDashboards && this.index < (this.settings.selectedDashboards?.length || 0)) {
            await this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, this.dashboard.DashboardKey, false);
        }
        else {
            await this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, null, false);
        }
    }

    //opens favorite tab
    openFavorites() {
        this.favoriteOpen = true;
        this.dashboardOpen = false;
        this.sortDashboardsLists();
        this.filterAvailableDashboards();
    }

    //opens dashboard tab
    openDashboards() {
        this.favoriteOpen = false;
        this.dashboardOpen = true;
        this.sortDashboardsLists();
        this.filterAvailableDashboards();
    }

    /** Called once at initialize */
    ngOnInit() {
        this.ngOnInitAsync();
    }

    async ngOnInitAsync() {
        await this.cvLocalizationService.GetSection('Dashboard_Projection');
        this.autoSwipeLabel1 = this.cvLocalizationService.getCachedText("Dashboard_Projection", "Auto_Swipe_Label_1", "Swipe between dashboards every");
        this.autoSwipeLabel2 = this.cvLocalizationService.getCachedText("Dashboard_Projection", "Auto_Swipe_Label_2", "minutes");
        // Load in validation messages
        this.validationErrors.requiredDashboard.message = this.cvLocalizationService.getCachedText(
            'Dashboard_Projection', 'Validation_Selected_Dashboard_Required', 'At least one dashboard must be selected');
        this.validationErrors.swipeTime.message = this.cvLocalizationService.getCachedText(
            'Dashboard_Projection', 'Validation_Delay_Too_Short', 'Minimum delay for autoswiping dashboards is 20 seconds');

        this.handleClockInterval();

        //Promises to load asynchronously
        const promises: [
            Promise<DashboardProjectionSettingsModel>,
            Promise<ApiResponse<DashboardsResponsiveExistingApiModel>>,
        ] = [
            // Load saved settings from state (api call)
            this.projectionService.loadCustomProjectionSettings(),
            // Loads the dashboards for this user into the current workspace
            this.dashboardService.loadExistingDashboardsAndGroups(),
        ];

        // Wait for all promises to resolve
        const promiseResults = await Promise.all(promises);

        // Load in values from state into the settings object
        this.settings = new DashboardProjectionSettingsModel();
        if (promiseResults[0] != null) {
            this.settings.selectedDashboards = promiseResults[0].selectedDashboards;
            this.settings.useAutoSwipe = promiseResults[0].useAutoSwipe;
            this.settings.rawMinutesInput = promiseResults[0].rawMinutesInput;
            this.settings.rawSecondsInput = promiseResults[0].rawSecondsInput;
        }

        this.settings.selectedDashboards.forEach(selectedDashKey => {
            const dash = this.dashboardService.availableDashboards.find(dash => dash.DashboardKey === selectedDashKey);
            if (dash !== null && dash !== undefined)
                this.settings.selectedDashboardObjects.push(dash);
        });
        this.dashboardService.dashboards = this.settings.selectedDashboardObjects;
        if (this.settings.selectedDashboards?.length > 0)
            this.dashboardService.activeDashboard.key = this.settings.selectedDashboardObjects[0]?.DashboardKey;
        this.validateLoadedSwipeTime();

        // Override some values on the settings object that don't come from state.
        // Make a copy of all available dashboards for this user in loadedDashboards
        this.settings.loadedDashboardObjects = this.dashboardService.availableDashboards.filter(d => d.IsCXOne);
        this.settings.loadedDashboards = this.settings.loadedDashboardObjects.map(d => d.DashboardKey);

        // Sort the dashboards by name, then id
        this.sortDashboardsLists();
        this.isLoading = false;

        if (this.settings.selectedDashboards.length === 0) {
            this.openSettingsModal();
        } else {
            this.initializeProjection();
        }

        this.placeholder = this.cvLocalizationService.getCachedText('Dashboard_Projection', 'Search_Dashboard', 'Search dashboard');
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(s => s?.unsubscribe());
    }

    public async initializeProjection() {
        this.hasInitialized = true;
        this.dashboardService.activeChanged.next([this.dashboardService.activeDashboard.key, this.dashboardService.activeDashboard.key]);
        this.isSettingsOpen = false;
        if (this.settings.useAutoSwipe) {
            this.restartAutoSwipeInterval();
            this.isPlaying = true;
        } else {
            this.isPlaying = false;
            this.lastIntervalStart = null;
            this.secondsElapsed = 0;
        }
    }

    /** Advance to the next dashboard in the  */
    public onNextClicked() {
        // Cancel the timer. That way the next auto-slide is at the normal interval. Otherwise, a 30 minute interval that is manually moved after 29 minutes will move again after 1 minute
        this.safeClearInterval();
        this.lastIntervalStart = Date.now();
        this.secondsElapsed = 0;

        // Advance to the next dashboard, wrapping back around before length is hit
        this.nextDashboard();

        // If the projection is playing, restart the interval so it will progress to the next dashboard after the time limit
        if (this.isPlaying)
            this.restartAutoSwipeInterval();
    }

    public onPrevClicked() {
        // If this is triggered manually, cancel the existing timer. This way if the next button is clicked just seconds away from an automatic one, it will restart the timer
        this.safeClearInterval();
        this.lastIntervalStart = Date.now();
        this.secondsElapsed = 0;

        // Backtrack to the previous dashboard
        this.previousDashboard();

        // If the projection is playing, restart the interval so it will progress to the next dashboard after the time limit
        if (this.isPlaying)
            this.restartAutoSwipeInterval();
    }
    

    /** Start auto-playing the dashboards with the specified delay. Calling this the projection is already playing will reset the timer */
    public startPlaying() {
        this.isPlaying = true;
        this.safeClearInterval();
        this.continueAutoSwipeInterval();
    }

    /** Stop auto-playing the dashboard projection */
    public stopPlaying() {
        this.isPlaying = false;
        this.safeClearInterval();
        this.saveElapsedInterval();
    }

    /** Opens the settings modal for the projections */
    public async openSettingsModal() {
        this.isSettingsOpen = true;

        const loadedSettings = await this.projectionService.loadCustomProjectionSettings();
        // remove deleted dashboards
        const keysToDelete = [];
        loadedSettings?.selectedDashboards.forEach(selectedDash => { 
            if (!this.dashboardService.availableDashboards.find(dash => dash.DashboardKey === selectedDash))
                keysToDelete.push(selectedDash);
        });
        keysToDelete.forEach(key => {
            loadedSettings?.selectedDashboards.splice(loadedSettings.selectedDashboards.indexOf(key), 1);
        });
        this.settings.selectedDashboards = loadedSettings?.selectedDashboards;

        this.safeClearInterval();
        if (this.isPlaying)
            this.saveElapsedInterval();
        if (this.settings)
            this.settings.copyToTemp();
        // set defaults
        const minutes = parseInt(this.settings.temp.rawMinutesInput.toString());
        const seconds = parseInt(this.settings.rawSecondsInput.toString());
        if (Number.isNaN(minutes) && Number.isNaN(seconds)) {
            this.settings.temp.rawMinutesInput = this.defaultSwipeMinutes;
            this.settings.temp.rawSecondsInput = this.defaultSwipeSeconds;
        }
        this.filteredDashboardsOnModal = this.settings.loadedDashboardObjects.slice();
        this.filterAvailableDashboards();
        this.modalRef = this.ngbModal.open(this.modalContentRef, this.ngbModalOptions);
        requestAnimationFrame(() => this.checkForSaveAllowed());
    }

    /** Saves the projection settings (by copying out of the .temp field into the top level properties) */
    public async saveProjectionSettings() {
        this.isSaving = true;
        this.settings.copyFromTemp();
        var settingsCopy = this.settings.copyForSave();
        await this.projectionService.saveCustomProjectionSettings(settingsCopy);
        this.dashboardService.dashboards = this.settings.selectedDashboardObjects;
        this.dashboardService.activeDashboard.key = this.settings.selectedDashboards[0];
        this.index = 0;
        this.closeSettings(true);
        this.modalRef.result.then(() => {
            setTimeout(() => this.isSaving = false, 500); // use a timeout to account for the modal close animation
        });
    }

    /** Closes the projection settings modal */
    public cancelProjectionSettings() {
        this.settings.selectedDashboardObjects = this.dashboardService.availableDashboards.filter(dash => this.settings?.selectedDashboards?.includes(dash.DashboardKey));
        this.closeSettings(false);
    }

    /** Selects a dashboard to add to the projection in the settings modal */
    public selectDashboardForProjection(dashboard: DashboardResponsiveApiModelSparse) {
        this.settings.temp.selectedDashboardObjects.push(dashboard);
        this.settings.temp.selectedDashboards.push(dashboard.DashboardKey);
        this.filterAvailableDashboards();
        this.sortDashboardsLists();
        this.checkForSaveAllowed();
        this.dashboardSelected = this.settings.temp.selectedDashboards.length === 0;
    }

    /** Removes a dashboard from the projection in the settings modal */
    public removeDashboardFromProjection(dashboard: DashboardResponsiveApiModelSparse) {
        const indexToRemove = this.settings.temp.selectedDashboards.indexOf(dashboard.DashboardKey);
        const objectIndexToRemove = this.settings.temp.selectedDashboardObjects.indexOf(dashboard);
        if (indexToRemove > -1) {
            this.settings.temp.selectedDashboardObjects.splice(objectIndexToRemove, 1);
            this.settings.temp.selectedDashboards.splice(indexToRemove, 1);
            this.filterAvailableDashboards();
            this.sortDashboardsLists();
            this.checkForSaveAllowed();
            this.dashboardSelected = this.settings.temp.selectedDashboards.length === 0;
        }
    }

    /** Removes all dashboards from the projection in the settings modal */
    public removeAllDashboardsFromProjection() {
        this.settings.temp.selectedDashboards = [];
        this.settings.temp.selectedDashboardObjects = [];
        this.dashboardSelected = true;
        this.filterAvailableDashboards();
        this.sortDashboardsLists();
        this.checkForSaveAllowed();
    }

    /** Filters the available dashboards so that only ones that match the search term and aren't in the selected list will show up */
    public filterAvailableDashboards() {
        if (this.settings?.loadedDashboardObjects?.length > 0) {
            this.filteredDashboardsOnModal = this.settings.loadedDashboardObjects?.filter(d =>
                (this.searchTerm === '' || d.DashboardName.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1) &&
                this.settings.temp.selectedDashboardObjects.map(sd => sd?.DashboardID).indexOf(d?.DashboardID) === -1);
                this.filteredDashboardsOnModal.sort((a, b) => this.sortDashboardsByName(a, b));             
            if (this.favoriteOpen) {
                this.filteredDashboardsOnModal = this.filteredDashboardsOnModal.filter(d => d.IsFavorite);
                this.filteredDashboardsOnModal.sort((a, b) => {
                    const dateA = new Date(a.LastViewDate || 0).getTime();
                    const dateB = new Date(b.LastViewDate || 0).getTime();
                    
                    if (dateA !== dateB) {
                        return dateB - dateA;
                    }
                    this.filteredDashboardsOnModal.sort((a, b) => this.sortDashboardsByName(a, b));
                });
            }
        }
        else this.filteredDashboardsOnModal = [];
    }

    sortDashboardsByName(a, b) {
        const lowerCaseComparison = a.DashboardName.toLowerCase().localeCompare(b.DashboardName.toLowerCase());
        if (lowerCaseComparison === 0) {
            return a.DashboardName.localeCompare(b.DashboardName);
        }                    
        return lowerCaseComparison;
    }

    public clearFilter() {
        this.searchTerm = '';
        if (this.favoriteOpen) {
            this.openFavorites();
        } else {
            this.filteredDashboardsOnModal = this.settings.loadedDashboardObjects.slice();
        }
    }

    /** Validate the minutes input, enforcing it to be between 00 and 99 and to always have 2 digits (or the placeholder text)
     *  This method will also auto-focus the seconds box when 2 digits are entered
     */
    public validateMinutesInput($event) {
        // Parse the value from the textbox (currentTarget.value) or default it to empty string
        let val = $event.currentTarget.value.length > 0 ? parseInt($event.currentTarget.value, 10) : '';

        // Keep the number within the 0 and 99 constraints
        if (typeof val === 'number') {
            if (val >= 99)
                val = 99;
            if (val < 0)
                val = 0;
        }

        // Set the value into the rawMinutesInput on the settings object (this is intentionally done before the 0 padding below)
        this.settings.temp.rawMinutesInput = val;

        // Get some stats about the value right now, such as length to determine if the next element needs to be focused or this element needs to be cleared out
        let stringVal = val.toString();
        let focusNextElement = false;
        const stringLength = stringVal.length;

        // If only one value is typed in, fill in a zero before the entered value
        if (stringLength === 1)
            stringVal = '0' + stringVal;

        // If two digits have been typed in, focus the next element (done at the bottom of this function)
        else if (stringLength === 2)
            focusNextElement = true;

        // If there is nothing entered in this box (likely backspace was pressed), then clear it out so the placeholder text can take over.
        else if (stringLength === 0)
            stringVal = '';

        // Set the value in the textbox back to the changed value (updates the textbox to match the model now)
        $event.currentTarget.value = stringVal;

        // Focus the next element if the flag was set above
        if (focusNextElement)
            $event.currentTarget.parentElement.children['seconds-input'].focus();

        // Make sure the the Save button is disabled/enabled based on these changes
        this.checkForSaveAllowed();
    }

    /** Validate the seconds input box, enforcing that it stays between 00 and 59 */
    public validateSecondsInput($event) {
        // Get the value from the textbox (currentTarget.value)
        let val = $event.currentTarget.value.length > 0 ? parseInt($event.currentTarget.value, 10) : '';

        // Limit the value to be between 0 and 59
        if (typeof val === 'number') {
            if (val >= 60)
                val = 59;
            if (val < 0)
                val = 0;
        }

        // Set the value into the settings value (this is done intentionally before the zero padding below)
        this.settings.temp.rawSecondsInput = val;

        let stringVal = val.toString();

        // If the length of the string is 1, pad it with a leading zero
        if (stringVal.length === 1)
            stringVal = '0' + stringVal;

        // If the length of the string is 0 (likely backspace was entered), clear out the value so the placeholder will show back up
        else if (stringVal.length === 0)
            stringVal = '';

        // Update the textbox with the transformed string value
        $event.currentTarget.value = stringVal;

        // Make sure that the Save button is disabled/enabled based on these changes
        this.checkForSaveAllowed();
    }

    /** Makes sure that if there is nothing entered in the minutes box on blur, it is filled in with 00 */
    public handleMinutesBlur($event) {
        if (this.settings.temp.rawMinutesInput === '' || $event.currentTarget.value === '')
            this.settings.temp.rawMinutesInput = $event.currentTarget.value = '00';

        this.checkForSaveAllowed();
    }

    /** Makes sure that if there is nothing entered in the seconds box on blur, it is filled in with 00 */
    public handleSecondsBlur($event) {
        if (this.settings.temp.rawSecondsInput === '' || $event.currentTarget.value === '')
            this.settings.temp.rawSecondsInput = $event.currentTarget.value = '00';

        this.checkForSaveAllowed();
    }

    /** Checks when the Use Auto Swipe checkbox is changed to update the Save button if needed */
    public handleAutoSwipeChanged() {
        this.checkForSaveAllowed();
    }

    private nextDashboard() {
        this.dashboardService.activeDashboard = null;
        this.index = ++this.index % this.settings.selectedDashboardObjects.length || 0;
        this.dashboardService.activeDashboard = { index: this.index, key: this.dashboard?.DashboardKey };
    }

    private previousDashboard() {
        this.dashboardService.activeDashboard = null;
        this.index = (--this.index + this.settings.selectedDashboardObjects.length) % this.settings.selectedDashboardObjects.length || 0;
        this.dashboardService.activeDashboard = { index: this.index, key: this.dashboard?.DashboardKey };
    }

    /** Ensures the initial time is two numbers long */
    private validateLoadedSwipeTime() {
        if (this.settings.rawMinutesInput.toString().length === 1)
            this.settings.rawMinutesInput = '0' + this.settings.rawMinutesInput.toString();
        if (this.settings.rawSecondsInput.toString().length === 1)
            this.settings.rawSecondsInput = '0' + this.settings.rawSecondsInput.toString();
    }        
            
    /** Track the time elapsed in the current interval  */
    private saveElapsedInterval() {
        if (this.lastIntervalStart != null)
            this.secondsElapsed = Date.now() - this.lastIntervalStart;
        else    
            this.secondsElapsed = 0;
    }

    /** Disables/enables the Save button based on the values entered in the modal. Also updates the validation error message */
    private checkForSaveAllowed() {
        this.settings.savingAllowed = true;
        this.validationErrors.requiredDashboard.showing = this.validationErrors.swipeTime.showing = false;

        const minutes = parseInt(this.settings.temp.rawMinutesInput.toString(), 10) || 0;
        const seconds = parseInt(this.settings.temp.rawSecondsInput.toString(), 10) || 0;
        const secondsToDelay = (minutes * 60) + seconds;

        const meetsMinimumTime = secondsToDelay >= this.minimumSwipeDelaySeconds;
        if (!meetsMinimumTime) {
            this.settings.savingAllowed = false;
            this.validationErrors.swipeTime.showing = true;
        }

        if ((this.settings.temp?.selectedDashboards?.length || 0) < 1) {
            this.settings.savingAllowed = false;
            this.validationErrors.requiredDashboard.showing = true;
        }
    }

    /** Handles closing the settings modal, and starting and stopping the projection */
    private closeSettings(saved: boolean) {
        if (saved)
            this.modalRef.close('saved');
        else 
            this.modalRef.close('canceled');

        this.hasInitialized = true;
        this.dashboardService.activeChanged.next([this.dashboardService.activeDashboard.key, this.dashboardService.activeDashboard.key]);
        this.isSettingsOpen = false;
        
        if ((this.settings?.selectedDashboards?.length ?? 0) > 0) {
            if (saved) {
                if (this.settings.temp.useAutoSwipe) {
                    this.restartAutoSwipeInterval();  
                    this.isPlaying = true;              
                }
                else {
                    this.isPlaying = false;
                    this.lastIntervalStart = null;
                    this.secondsElapsed = 0;
                }
            }
            else {
                if (this.isPlaying)
                    this.continueAutoSwipeInterval();
            }
        }
    }

    /** If the interval timer (the javascript interval) for auto-swipe is not null, clear it */
    private safeClearInterval() {
        if (this.intervalTimer != null) {
            this.$window.clearInterval(this.intervalTimer);
            this.intervalTimer = null;
        }
    }

    /** Restarts the interval timer (the javascript interval) for auto-swipe using settings.autoSwipeDelay */
    private restartAutoSwipeInterval() {
        const minutes = parseInt(this.settings.rawMinutesInput.toString(), 10) || 0;
        const seconds = parseInt(this.settings.rawSecondsInput.toString(), 10) || 0;
        const secondsToDelay = Math.max((minutes * 60) + seconds, this.minimumSwipeDelaySeconds);

        this.intervalTimer = this.$window.setInterval(this.nextDashboard.bind(this), secondsToDelay * 1000);
        this.lastIntervalStart = Date.now();
        this.secondsElapsed = 0;
    }

    /** Continues the timer with the remaining time from the last auto-swipe interval */
    private continueAutoSwipeInterval() {
        const minutes = parseInt(this.settings.rawMinutesInput.toString(), 10) || 0;
        const seconds = parseInt(this.settings.rawSecondsInput.toString(), 10) || 0;
        const secondsToDelay = Math.max((minutes * 60) + seconds, this.minimumSwipeDelaySeconds);
        if (secondsToDelay <= this.secondsElapsed / 1000) 
            this.secondsElapsed = 0;

        this.intervalTimer = this.$window.setInterval(this.nextDashboard.bind(this), secondsToDelay * 1000 - this.secondsElapsed);
    }

    /** Restarts the interval counting down the clock on the screen */
    private handleClockInterval() {
        this.$window.clearInterval(this.dashboardClockTimer);
        this.dashboardClockTimer = this.$window.setInterval(() => 
            { 
                this.dashboardTime = moment().add(this.timezoneOffset, 'hours').toString();
                this.handleClockInterval();
            }, 1000);
    }

    /** Sort the loaded dashboards lists (both settings and settings.temp collections) */
    private sortDashboardsLists() {
        if (this.settings) {
            this.settings.loadedDashboardObjects?.sort(this.__compareDashboardsByNameThenId);
            if (this.favoriteOpen)
                this.settings.loadedDashboardObjects?.sort(this.__compareDashboardsByLastViewDate);

            if (this.settings.temp != null) {
                this.settings.temp.loadedDashboardObjects?.sort(this.__compareDashboardsByNameThenId);
                if (this.favoriteOpen)
                    this.settings.temp.loadedDashboardObjects?.sort(this.__compareDashboardsByLastViewDate);
            }
        }
    }

    /** Compares dashboards first by name, then by dashboard Id if there is a collision */
    private __compareDashboardsByNameThenId = (a: Dashboard, b: Dashboard) => {
        // Compare names first.
        if (a.DashboardName.toLowerCase() < b.DashboardName.toLowerCase())
            return -1;
        if (a.DashboardName.toLowerCase() > b.DashboardName.toLowerCase())
            return 1;

        // If two match, return by IDs to keep the lists consistent
        if (a.DashboardID < b.DashboardID)
            return -1;
        if (a.DashboardID > b.DashboardID)
            return 1;

        // Should never hit this, but just in case
        return 0;
    };

    /** Compares dashboards by last view date descending */
    private __compareDashboardsByLastViewDate = (a: Dashboard, b: Dashboard) => {
        if (a.LastViewDate < b.LastViewDate)
            return 1;
        if (a.LastViewDate > b.LastViewDate)
            return -1;
        
        return 0;
    }
}
